import { useState, useEffect, useMemo } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import {
  Grid,
  Paper,
  TableContainer,
  Table,
  TableBody,
  TableRow,
  TableCell,
  Tooltip,
} from '@mui/material'
import BlockIcon from '@mui/icons-material/Block'
import CheckIcon from '@mui/icons-material/Check'
import ClearIcon from '@mui/icons-material/Clear'
import AccessTimeIcon from '@mui/icons-material/AccessTime'

import Select from '../components/Select'
import GridHeader from '../components/GridHeader'
import TableCellTeam from '../components/table/TableCellTeam'
import ButtonRunDownload from '../components/ButtonRunDownload'
import SelectionBar from '../components/selection/SelectionBar'
import SortableTableHead from '../components/SortableTableHead'
import DialogDesignazione from '../components/DialogDesignazione'
import GridCircularProgress from '../components/GridCircularProgress'
import { config } from '../settings/configs'
import {
  GIORNI,
  colours,
  titlesDesignazioni as titles,
  phrasesDesignazione as phrases,
} from '../settings/settings'
import { useFeedback } from '../hooks/feedback-hook'
import {
  useGetArbitriQuery,
  useGetCalendariRefereeQuery,
  useGetCalendariManyQuery,
  useGetDesignazioniQuery,
  useCreateDesignazioneMutation,
  useUpdateDesignazioneReplyMutation,
  useGetIscrizioniQuery,
  useGetSpostamentiWaitingQuery,
} from '../hooks/api-slice'
import { sortNumeriGara, sortDataOra, sortAuto } from '../hooks/sort-functions'
import {
  fromStringsToDateTime,
  filterByNumeroGara,
  filterByPeriod,
  filterByDesStatus,
} from '../hooks/utils-functions'
import { setSelectedNumeriGara } from '../container/selection'
import { setDesignazione, setSelectedGara } from '../container/designazione'

const STATUSES0 = ['Tutte', 'In sospeso', 'Accettate', 'Rifiutate']

const Designazioni = () => {
  const { admin, arbitro, designatore, idArbitro } = useSelector(
    state => state.home.infoUtente,
  )
  const selectedStagione = useSelector(
    state => state.selection.selectedStagione,
  )
  const selectedCampionato = useSelector(
    state => state.selection.selectedCampionato,
  )
  const selectedCategoria = useSelector(
    state => state.selection.selectedCategoria,
  )
  const selectedNumeroGara = useSelector(
    state => state.selection.selectedNumeroGara,
  )
  const availablePeriodi = useSelector(
    state => state.selection.availablePeriodi,
  )
  const selectedPeriodo = useSelector(state => state.selection.selectedPeriodo)
  const selectedArbitroB = useSelector(state => state.selection.selectedArbitro)
  const { data: arbitri } = useGetArbitriQuery(undefined, {
    skip: !(admin || designatore),
  })
  const designazione = useSelector(state => state.designazione.designazione)
  const selectedGara = useSelector(state => state.designazione.selectedGara)
  const selectedArbitro1 = useSelector(
    state => state.designazione.selectedArbitro1,
  )
  const selectedArbitro2 = useSelector(
    state => state.designazione.selectedArbitro2,
  )
  const checkAccept = useSelector(state => state.designazione.checkAccept)
  const checkReject = useSelector(state => state.designazione.checkReject)
  const sortingColumn = useSelector(state => state.sorting.designazioni?.column)
  const sortingAscending = useSelector(
    state => state.sorting.designazioni?.ascending,
  )

  const { data: calendariAdmin = [], isFetching: isFetchingR } =
    useGetCalendariRefereeQuery(selectedCampionato, {
      skip: !((admin || designatore) && selectedCampionato),
    })

  const iArgs = arbitro
    ? { stagione: selectedStagione }
    : { campionato: selectedCampionato }
  const { isFetching: isFetchingI } = useGetIscrizioniQuery(
    { ...iArgs, iFilter: 'nomi' },
    { skip: !(arbitro ? selectedStagione : selectedCampionato) },
  )

  const url =
    admin || designatore
      ? '/all'
      : `/stagione/${selectedStagione}/arbitro/${idArbitro}` // get "all" to correctly check overlaps
  const { data: designazioni, isFetching: isFetchingD } =
    useGetDesignazioniQuery(url, {
      skip: !(
        admin ||
        designatore ||
        (arbitro && selectedStagione && idArbitro)
      ),
    })

  const idGare = designazioni?.map(d => d.idGara)
  const { data: calendariArbitro = [], isFetching: isFetchingA } =
    useGetCalendariManyQuery(idGare, { skip: !(arbitro && idGare?.length) })

  const calendari = arbitro ? calendariArbitro : calendariAdmin

  const { data: waitingSpostamenti } = useGetSpostamentiWaitingQuery(
    selectedCampionato,
    { skip: !selectedCampionato },
  )

  const [createDesignazioneMutation] = useCreateDesignazioneMutation()
  const [updateDesignazioneReplyMutation] = useUpdateDesignazioneReplyMutation()

  const dispatch = useDispatch()

  document.title = `${config?.name} - Designazioni`

  let columnsDesignazioni = [
    { label: 'Categoria', sortable: true, sortingField: 'categoria' },
    { label: 'Gara', sortable: true, sortingField: 'numeroGara' },
    { label: 'Data e Ora', sortable: true, sortingField: 'dataoragara' },
  ]
  if (admin || designatore)
    columnsDesignazioni = [
      ...columnsDesignazioni,
      { label: '1° Arbitro', sortable: true, sortingField: 'cognomenome1' },
      { label: '2° Arbitro', sortable: true, sortingField: 'cognomenome2' },
    ]
  if (arbitro)
    columnsDesignazioni = [
      ...columnsDesignazioni,
      { label: 'Squadra in casa', sortable: false },
      { label: 'Squadra in trasferta', sortable: false },
      { label: 'Ruolo', sortable: false },
      { label: 'Stato', sortable: false },
    ]

  let STATUSES = STATUSES0
  if (admin || designatore) STATUSES = [...STATUSES0, 'Da designare']
  if (arbitro) STATUSES = [...STATUSES0, 'Annullate']
  const [selectedStatus, setSelectedStatus] = useState(STATUSES[0])

  const [mode, setMode] = useState()

  const [openDesignazione, setOpenDesignazione] = useState()

  const { setFeedback } = useFeedback()

  const handleClickOpenDesignazione = () => setOpenDesignazione(true)

  const handleCloseDesignazione = () => {
    setOpenDesignazione()
    dispatch(setSelectedGara())
    dispatch(setDesignazione())
    setMode()
  }

  const mergedData = useMemo(() => {
    let mData
    if (admin || designatore) {
      const mCal = calendari?.map(x => {
        return {
          idGara: x.id,
          categoria: x.categoria,
          numeroGara: x.numeroGara,
          data: x.data,
          ora: x.ora,
        }
      })

      const mDes = designazioni
        ?.filter(x => x.codiceCampionato === selectedCampionato)
        ?.map(x => {
          return {
            idDesignazione: x.id,
            idArbitro: x.idArbitro,
            idGara: x.idGara,
            categoria: x.categoria,
            numeroGara: x.numeroGara,
            isValid: x.isValid,
            isSecondoArbitro: x.isSecondoArbitro,
            acceptedByArbitro: x.acceptedByArbitro,
            timeDomanda: x.timeDomanda,
          }
        })

      const mArb = arbitri?.map(x => {
        return { idArbitro: x.id, cognomenome: `${x.cognome} ${x.nome}` }
      })

      mData = mCal?.map(c => {
        const d1 = mDes
          ?.filter(x => x.idGara === c?.idGara && !x.isSecondoArbitro)
          ?.map(x => {
            return {
              idDesignazione1: x.idDesignazione,
              idArbitro1: x.idArbitro,
              idGara: x.idGara,
              isValid1: x.isValid,
              acceptedByArbitro1: x.acceptedByArbitro,
              timeDomanda1: x.timeDomanda,
            }
          })
          ?.sort((a, b) => (a.timeDomanda1 < b.timeDomanda1 ? 1 : -1))?.[0]

        const d2 = mDes
          ?.filter(x => x.idGara === c?.idGara && x.isSecondoArbitro)
          ?.map(x => {
            return {
              idDesignazione2: x.idDesignazione,
              idArbitro2: x.idArbitro,
              isValid2: x.isValid,
              acceptedByArbitro2: x.acceptedByArbitro,
              timeDomanda2: x.timeDomanda,
            }
          })
          ?.sort((a, b) => (a.timeDomanda2 < b.timeDomanda2 ? 1 : -1))?.[0]

        const a1 = mArb
          ?.filter(x => x.idArbitro === d1?.idArbitro1)
          ?.map(x => {
            return { cognomenome1: x.cognomenome }
          })?.[0]

        const a2 = mArb
          ?.filter(x => x.idArbitro === d2?.idArbitro2)
          ?.map(x => {
            return { cognomenome2: x.cognomenome }
          })?.[0]

        return { ...c, ...d1, ...d2, ...a1, ...a2 }
      })
    } else {
      const mCal = calendari?.map(x => {
        return {
          idGara: x.id,
          categoria: x.categoria,
          numeroGara: x.numeroGara,
          data: x.data,
          ora: x.ora,
          squadraCasaID: x.squadraCasaID,
          squadraTrasfertaID: x.squadraTrasfertaID,
        }
      })

      const mDes = designazioni?.map(x => {
        return {
          idDesignazione: x.id,
          idArbitro: x.idArbitro,
          idGara: x.idGara,
          categoria: x.categoria,
          numeroGara: x.numeroGara,
          isValid: x.isValid,
          isSecondoArbitro: x.isSecondoArbitro,
          acceptedByArbitro: x.acceptedByArbitro,
          timeDomanda: x.timeDomanda,
        }
      })

      mData = mDes?.map(d => {
        const c = mCal?.find(x => x.idGara === d?.idGara)
        return { ...c, ...d }
      })
    }

    return mData
      ?.filter(
        x =>
          arbitro ||
          x.categoria === selectedCategoria ||
          selectedCategoria === 'Tutte' || // avoid availableCategorie[0] to prevent infinite loop
          !selectedCategoria,
      )
      ?.sort(
        (a, b) =>
          sortDataOra(a.data, a.ora, b.data, b.ora) *
            (admin || designatore ? 1 : -1) ||
          sortNumeriGara(a.numeroGara, b.numeroGara),
      )
  }, [
    admin,
    arbitro,
    arbitri,
    calendari,
    designatore,
    designazioni,
    selectedCampionato,
    selectedCategoria,
  ])

  const mergedDataN = sortAuto(
    filterByNumeroGara(
      filterByPeriod(mergedData, availablePeriodi, selectedPeriodo),
      selectedNumeroGara,
    )?.map(d => {
      return {
        ...d,
        dataoragara: `${fromStringsToDateTime(d?.data, d?.ora)?.getTime()}-${
          d?.numeroGara
        }`,
      }
    }),
    sortingColumn,
    sortingAscending,
  )

  // update numeri gara for Excel
  useEffect(() => {
    dispatch(
      setSelectedNumeriGara(
        filterByDesStatus(
          mergedDataN?.filter(
            m =>
              !selectedArbitroB ||
              (selectedArbitroB === m.idArbitro1 && m.isValid1) ||
              (selectedArbitroB === m.idArbitro2 && m.isValid2),
          ),
          STATUSES,
          selectedStatus,
          admin || designatore,
          arbitro,
        )?.map(d => d.numeroGara),
      ),
    )
  }, [
    STATUSES,
    admin,
    arbitro,
    designatore,
    dispatch,
    mergedDataN,
    selectedArbitroB,
    selectedStatus,
  ])

  const performDesignazione = async () => {
    const infoOk =
      !!selectedGara?.id && (!!selectedArbitro1?.id || !!selectedArbitro2?.id)
    if (!infoOk) {
      setFeedback('Gara o arbitri non trovati!')
      return
    }

    const garaData = mergedDataN?.find(x => x.idGara === selectedGara?.id)
    const arbitriOk =
      selectedArbitro1?.id !== selectedArbitro2?.id &&
      (!selectedArbitro1?.id ||
        !garaData?.isValid2 ||
        selectedArbitro1?.id !== garaData?.idArbitro2 ||
        selectedArbitro2?.id) &&
      (!selectedArbitro2?.id ||
        !garaData?.isValid1 ||
        selectedArbitro2?.id !== garaData?.idArbitro1 ||
        selectedArbitro1?.id)
    if (!arbitriOk) {
      setFeedback('Il 1° arbitro non può fare anche il 2°!')
      return
    }

    if (selectedArbitro1?.id) {
      const body = {
        idGara: selectedGara.id,
        idArbitro: selectedArbitro1.id,
      }
      const result = await createDesignazioneMutation(body)
      if (result?.error) {
        setFeedback(`1° arbitro: ${result?.error?.data?.message}`)
        return
      }
    }

    if (selectedArbitro2?.id) {
      const body = {
        idGara: selectedGara.id,
        idArbitro: selectedArbitro2.id,
        isSecondoArbitro: true,
      }
      const result = await createDesignazioneMutation(body)
      if (result?.error) {
        setFeedback(`2° arbitro: ${result?.error?.data?.message}`)
        return
      }
    }

    handleCloseDesignazione()
    setFeedback(
      phrases.phraseDesignazioneDomandaSuccess,
      false,
      titles.titleDesignazioneDomanda,
    )
  }

  const replyDesignazione = async () => {
    if (!checkAccept && !checkReject) {
      setFeedback('La designazione va accettata o rifiutata.')
      return
    }

    const arg = {
      id: designazione?._id,
      reply: checkAccept ? 'accetta' : 'rifiuta',
    }
    const result = await updateDesignazioneReplyMutation(arg)

    if (!result?.error) {
      setFeedback(
        (checkAccept && phrases.phraseDesignazioneAccetta) ||
          (checkReject && phrases.phraseDesignazioneRifiuta),
        false,
        (checkAccept && titles.titleDesignazioneAccetta) ||
          (checkReject && titles.titleDesignazioneRifiuta),
      )
      handleCloseDesignazione()
    }
  }

  const DesignazioniTable = ({ data }) => {
    // variables without number are for arbitro
    // variables with 1/2 number are for admin
    const hasDate = data?.data && data?.ora
    const isValid = data?.isValid === true
    const isValid1 = data?.isValid1 === true
    const isValid2 = data?.isValid2 === true
    const isAccepted = data?.acceptedByArbitro === true
    const isAccepted1 = data?.acceptedByArbitro1 === true
    const isAccepted2 = data?.acceptedByArbitro2 === true
    const isRejected = data?.acceptedByArbitro === false
    const isRejected1 = data?.acceptedByArbitro1 === false
    const isRejected2 = data?.acceptedByArbitro2 === false
    const isPending = ![true, false].includes(data?.acceptedByArbitro)
    const isPending1 = ![true, false].includes(data?.acceptedByArbitro1)
    const isPending2 = ![true, false].includes(data?.acceptedByArbitro2)
    const isReplyable = data?.idDesignazione && isValid && isPending
    const isReplyable1 = data?.idDesignazione1 && isValid1 && isPending1
    const isReplyable2 = data?.idDesignazione2 && isValid2 && isPending2
    return (
      <TableRow
        sx={
          arbitro || hasDate
            ? {
                cursor: (admin || designatore || isValid) && 'pointer',
                backgroundColor: waitingSpostamenti?.includes(data.idGara)
                  ? colours.redError
                  : isReplyable || isReplyable1 || isReplyable2
                    ? colours.highlight
                    : colours.white,
                ':hover': { backgroundColor: colours.greyVeryLight },
              }
            : {
                backgroundColor: waitingSpostamenti?.includes(data.idGara)
                  ? colours.redError
                  : colours.greyVeryLight,
              }
        }
        onClick={() => {
          if (admin || designatore ? hasDate : isValid) {
            dispatch(
              setSelectedGara(calendari?.find(x => x.id === data?.idGara)),
            )
            if (admin || designatore) {
              setMode('update')
            }
            if (arbitro) {
              setMode(isReplyable ? 'reply' : 'view')
              dispatch(
                setDesignazione(
                  designazioni?.find(x => x.id === data?.idDesignazione),
                ),
              )
            }
            handleClickOpenDesignazione()
          }
        }}
      >
        <TableCell>{data?.categoria}</TableCell>
        <TableCell>{data?.numeroGara}</TableCell>
        <TableCell>
          {hasDate && (admin || designatore || isValid)
            ? `${GIORNI[
                (fromStringsToDateTime(data?.data, data?.ora)?.getDay() -
                  1 +
                  GIORNI.length) %
                  GIORNI.length
              ]
                ?.slice(0, 2)
                ?.toUpperCase()} ${data?.data} ${data?.ora}`
            : '-'}
        </TableCell>
        {(admin || designatore) && (
          <TableCell>
            {data?.idDesignazione1 && (
              <Grid container alignItems='center' sx={{ width: '100%' }}>
                <Grid>
                  <Tooltip
                    placement='right'
                    title={
                      (isValid1 && isAccepted1 && 'Accettata') ||
                      (isValid1 && isPending1 && 'In sospeso') ||
                      (!isValid1 && isRejected1 && 'Rifiutata') ||
                      (!isValid1 && 'Annullata') ||
                      'Sconosciuto'
                    }
                  >
                    {(isValid1 && isAccepted1 && (
                      <CheckIcon color='success' />
                    )) ||
                      (isValid1 && isPending1 && (
                        <AccessTimeIcon color='warning' />
                      )) ||
                      (!isValid1 && isRejected1 && (
                        <ClearIcon color='error' />
                      )) ||
                      (!isValid1 && <BlockIcon color='error' />) || <></>}
                  </Tooltip>
                </Grid>
                <Grid size='grow'>
                  {data?.cognomenome1 || <i>Nome non trovato</i>}
                </Grid>
              </Grid>
            )}
          </TableCell>
        )}
        {(admin || designatore) && (
          <TableCell>
            {data?.idDesignazione2 && (
              <Grid container alignItems='center' sx={{ width: '100%' }}>
                <Grid>
                  <Tooltip
                    placement='right'
                    title={
                      (isValid2 && isAccepted2 && 'Accettata') ||
                      (isValid2 && isPending2 && 'In sospeso') ||
                      (!isValid2 && isRejected2 && 'Rifiutata') ||
                      (!isValid2 && 'Annullata') ||
                      'Sconosciuto'
                    }
                  >
                    {(isValid2 && isAccepted2 && (
                      <CheckIcon color='success' />
                    )) ||
                      (isValid2 && isPending2 && (
                        <AccessTimeIcon color='warning' />
                      )) ||
                      (!isValid2 && isRejected2 && (
                        <ClearIcon color='error' />
                      )) ||
                      (!isValid2 && <BlockIcon color='error' />) || <></>}
                  </Tooltip>
                </Grid>
                <Grid size='grow'>
                  {data?.cognomenome2 || <i>Nome non trovato</i>}
                </Grid>
              </Grid>
            )}
          </TableCell>
        )}
        {arbitro && (
          <TableCellTeam id={data?.squadraCasaID} stagione={selectedStagione} />
        )}
        {arbitro && (
          <TableCellTeam
            id={data?.squadraTrasfertaID}
            stagione={selectedStagione}
          />
        )}
        {arbitro && (
          <TableCell>
            {data?.isSecondoArbitro ? '2° arbitro' : '1° arbitro'}
          </TableCell>
        )}
        {arbitro && (
          <TableCell>
            {data?.idDesignazione && (
              <Grid>
                <Tooltip
                  placement='right'
                  title={
                    (isValid && isAccepted && 'Accettata') ||
                    (isValid && isPending && 'In sospeso') ||
                    (!isValid && isRejected && 'Rifiutata') ||
                    (!isValid && 'Annullata') ||
                    'Sconosciuto'
                  }
                >
                  {(isValid && isAccepted && <CheckIcon color='success' />) ||
                    (isValid && isPending && (
                      <AccessTimeIcon color='warning' />
                    )) ||
                    (!isValid && isRejected && <ClearIcon color='error' />) ||
                    (!isValid && <BlockIcon color='error' />) || <></>}
                </Tooltip>
              </Grid>
            )}
          </TableCell>
        )}
      </TableRow>
    )
  }

  return (
    <Grid container paddingX={2} spacing={2} sx={{ width: '100%' }}>
      <GridHeader h2 text='Designazioni' />
      <Grid container alignItems='center' sx={{ width: '100%' }}>
        <SelectionBar
          data={calendari}
          yStagione={arbitro}
          yCampionato={admin || designatore}
          yCategoria={admin || designatore}
          yNumeroGara={admin || designatore}
          yPeriodo
          yArbitro={admin || designatore}
        />
        <Grid size={{ xs: 12, md: 3 }}>
          <Select
            label='Stato'
            value={selectedStatus}
            options={STATUSES}
            onChange={setSelectedStatus}
          />
        </Grid>
        {(admin || designatore) && (
          <Grid size={{ xs: 12, md: 'grow' }}>
            <ButtonRunDownload
              buttonText='Scarica Excel'
              algorithm='12_GenerateExcelDesignazioni'
              type='XLSX'
              disabled={!selectedCampionato || !mergedDataN?.length}
            />
          </Grid>
        )}
      </Grid>
      {((isFetchingD || isFetchingR || isFetchingA || isFetchingI) && (
        <GridCircularProgress />
      )) ||
        (!mergedData && (
          <GridHeader
            text={`Elenco delle ${admin || designatore ? 'partite' : 'designazioni'} non trovato.`}
          />
        )) ||
        (!mergedDataN?.length && (
          <GridHeader
            text={`Nessuna ${admin || designatore ? 'partita' : 'designazione'} trovata.`}
          />
        )) || (
          <Grid container paddingY={2} sx={{ width: '100%' }}>
            <Grid size={12}>
              {config?.features?.spostamenti && (
                <>
                  Le gare evidenziate in rosso hanno uno spostamento in sospeso.
                  <br />
                </>
              )}
              Le gare evidenziate in giallo sono in attesa della conferma
              dell'arbitro.
              {(admin || designatore) && (
                <>
                  <br />
                  Le gare evidenziate in grigio sono senza data e ora.
                </>
              )}
            </Grid>
            <TableContainer component={Paper}>
              <Table size='small'>
                <SortableTableHead
                  table={mergedDataN}
                  columns={columnsDesignazioni}
                  tableName='designazioni'
                />
                <TableBody>
                  {filterByDesStatus(
                    mergedDataN?.filter(m =>
                      selectedArbitroB
                        ? [m.idArbitro1, m.idArbitro2].includes(
                            selectedArbitroB,
                          )
                        : true,
                    ),
                    STATUSES,
                    selectedStatus,
                    admin || designatore,
                    arbitro,
                  )?.map((m, index) => (
                    <DesignazioniTable key={index} data={m} />
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Grid>
        )}
      <DialogDesignazione
        open={openDesignazione}
        handleClose={handleCloseDesignazione}
        triggerFunction={
          (mode === 'update' && performDesignazione) ||
          (mode === 'reply' && replyDesignazione) ||
          handleCloseDesignazione
        }
        designazioni={designazioni}
        mode={mode}
      />
    </Grid>
  )
}

export default Designazioni
